import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, effect, inject, model, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FieldType, FieldTypeConfig, FormlyFieldConfig, FormlyFormBuilder, FormlyModule } from '@ngx-formly/core';
import { simpleDeepClone } from '@phx-erp/shared';
import { TabsModule } from "primeng/tabs";
import { lastValueFrom } from "rxjs";
import { CustomFieldsFormConfig, CustomFieldsHelperService } from '../../../@core/services/custom-fields-helper.service';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { applyFieldConfig, findFieldByKey } from '../../../common/utilities/ngx-formly.helper';
import { GET_CUSTOM_ENTITY_SCHEMAS_WITH_COLUMNS } from "../../../data/definitions/custom-entity-schema.definitions";
import { ServerConfigService } from "../../../data/server-config";
import { CustomEntityColumnSchema, CustomEntitySchema, CustomFields, IBaseModel, Operator, SearchRule } from "../../../generated/generated-types";
import { CustomEntityColumnSchemaService } from "../../../modules/admin/services/custom-entity-column-schema.service";
import { CustomEntitySchemaService } from "../../../modules/admin/services/custom-entity-schema.service";
import { CustomEntityListWidgetComponent } from '../../../modules/custom-entity/components/custom-entity-list-widget/custom-entity-list-widget.component';
import { CustomEntityService } from "../../../modules/custom-entity/services/custom-entity.service";
import { BlockableDiv } from '../../controls/blockable-div/blockable-div.component';
import { ICustomFieldsFormWidget } from './ICustomFieldsFormWidget';

/**
 * Properties used for this.field.props by this component
 */
type ComponentFieldProps = {
  /** (required) the type of which the custom fields should be displayed (e.g. 'Product', 'Document',...) */
  type: string;
  /** if the header is shown (default: false) */
  showHeader?: boolean | "true" | "false";
  /** the text used as a header (default: document.custom-fields) */
  headerText?: string;
  /** if the default custom fields are displayed (default: false) */
  hideDefaultFields?: boolean | "true" | "false";
  /** fields to be displayed */
  fieldWhitelist?: string[];
  /** fields not to be displayed */
  fieldBlacklist?: string[];
}


type CustomEntityTabInfo = {
  tabLabel: string;
  entityName: string;
  entitySchema: CustomEntitySchema;
  silentSearchRules: () => SearchRule[];
  silentDefaultModel: () => IBaseModel;
}

@Component({
  selector: 'app-custom-fields-form-widget',
  templateUrl: './custom-fields-form-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FormlyModule,
    TabsModule,
    BlockableDiv,
    CustomEntityListWidgetComponent,
  ],
  providers: [
    CustomEntityService,
  ]
})
export class CustomFieldsFormWidgetComponent extends FieldType<FieldTypeConfig> implements AfterViewInit, ICustomFieldsFormWidget {

  fields = signal<FormlyFieldConfig[]>([]);

  relatedCustomEntities = signal<CustomEntityTabInfo[]>(null);
  selectedCustomEntityTab = signal<number>(0);

  modelExists = model(false);

  destroyRef = inject(DestroyRef);
  builder = inject(FormlyFormBuilder);


  // Custom fields service instance
  customFieldsService = inject(CustomFieldsHelperService);

  constructor(
    protected serverConfigService: ServerConfigService,
    protected customEntitySchemaService: CustomEntitySchemaService,
    protected customEntityColumnSchemaService: CustomEntityColumnSchemaService,
    protected customEntityService: CustomEntityService,
    private i18n: I18nService
  ) {
    super();

    // Watch entityFieldConfigs and apply configs to custom fields
    effect(() => {
      const entityFieldConfigs = this.formState.detailComponent.entityConfig()?.entityFieldConfigs;
      const disableRequiredInEntityFieldConfig = this.formState.detailComponent.disableRequiredInEntityFieldConfig();
      const currentFields = this.fields();

      untracked(() => {
        if (entityFieldConfigs && entityFieldConfigs.length > 0 && currentFields.length > 0) {
          // Apply configs to custom fields only
          for (const config of entityFieldConfigs) {
            if (config.field?.startsWith('customFields.')) {
              const field = findFieldByKey(currentFields, config.field);
              if (field) {
                applyFieldConfig(field, config, !!this.model?.id, disableRequiredInEntityFieldConfig);
              }
            }
          }
        }
        this.builder.buildForm(this.form, this.fields(), this.model, this.options);
      });
    });
  }

  async ngAfterViewInit() {

    this.props.instance = this;

    // Get custom fields from server
    const cf = await this.serverConfigService.getCustomFieldsFor(this.field.props.type as Exclude<keyof CustomFields, '__typename'>);

    if (cf?.length <= 0)
      return;

    // Extract ComponentFieldProps from this.field.props
    const fieldProps = this.field.props as ComponentFieldProps;

    // Configure the service
    const directiveConfig: CustomFieldsFormConfig = {
      type: fieldProps.type,
      fieldWhitelist: fieldProps.fieldWhitelist,
      fieldBlacklist: fieldProps.fieldBlacklist,
      hideDefaultFields: fieldProps.hideDefaultFields === true || fieldProps.hideDefaultFields == "true",
      wrappersForFields: this.field.props.wrappersForFields
    };

    // Use the service to generate custom fields (this handles all filtering and field generation)
    await this.customFieldsService.generateCustomFields(cf, directiveConfig);

    // Get the generated fields from the service
    const result = this.customFieldsService.generatedFields();

    this.fields.set([
      {
        fieldGroupClassName: "flex flex-wrap",
        props: this.getHeaderProps(),
        wrappers: ['simple-title'],
        fieldGroup: result
      }
    ]);

    this.getCustomEntityRelations();

    // !!! this is reeeally bad but right now i dont see another way to do it (and we really need to wait for model to be set)

    while (true) {
      if (Object.keys(this.model).length > 0) {
        this.modelExists.set(true);
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }

  }

  private getHeaderProps(): any {
    const fieldProps = this.field.props as ComponentFieldProps;

    if (fieldProps.showHeader === true || fieldProps.showHeader == "true") {
      return {
        label: fieldProps.headerText ?? _('document.custom-fields'),
        translate: true,
        hrBelowTitle: true,
      };
    }

    return {};
  }

  async getCustomEntityRelations() {
    //entity approach:
    /* 
      let entities = (await lastValueFrom(this.customEntitySchemaService.getItems({
        searchFilter: {
          rules: [
            {
              field: "columns.relatedToEntity",
              operator: Operator.equal,
              value: (this.props as ComponentFieldProps).type
            },
            {
              field: "columns.relationType",
              operator: Operator.equal,
              value: "ManyToOne"
            }
          ]
        },
      }, {
        fragment: GET_CUSTOM_ENTITY_SCHEMAS_WITH_COLUMNS
      }).single$))?.getCustomEntitySchemas?.items;

      let values = entities.map((e: CustomEntitySchema) => {
        let relatedColumns = e.columns
          .filter(c => c.relationType == "ManyToOne" && c.relatedToEntity == (this.props as ComponentFieldProps).type);

        return {
          tabLabel: e.displayName ?? e.identifier,
          entityName: e.identifier,
          entitySchema: e,
          silentSearchRules: () => relatedColumns.map(c => (
            {
              field: c.identifier,
              operator: Operator.equal,
              value: this.model?.id
            }
          )),
          silentDefaultModel: () => {
            let model = {};
            for (let c of relatedColumns) {
              model[c.identifier] = this.model?.id;
            }
            return model;
          }
        } as CustomEntityTabInfo;
      });

      this.relatedCustomEntities.set(values);
      return values; 
    */

    //column approach:
    let columnsResult = await lastValueFrom(this.customEntityColumnSchemaService.getItems({
      searchFilter: {
        rules: [
          {
            field: "relatedToEntity",
            operator: Operator.equal,
            value: (this.props as ComponentFieldProps).type
          },
          {
            field: "relationType",
            operator: Operator.equal,
            value: "ManyToOne"
          },
          {
            field: "entitySchema.deleted",
            operator: Operator.notequal,
            value: JSON.stringify(true)
          },
          {
            field: "deleted",
            operator: Operator.notequal,
            value: JSON.stringify(true)
          }
        ]
      },
    }).single$);
    let columns = simpleDeepClone(columnsResult?.getCustomEntityColumnSchemas?.items as CustomEntityColumnSchema[] ?? []);

    if (!columns || columns.length <= 0)
      return;

    let entities = (await lastValueFrom(this.customEntitySchemaService.getItems({
      searchFilter: {
        rules: [
          {
            field: "id",
            operator: Operator.in,
            value: JSON.stringify(columns
              .filter(c => c.entitySchemaId && !c.entitySchema)
              .map(c => c.entitySchemaId)
            ),
          }
        ]
      },
    }, {
      fragment: GET_CUSTOM_ENTITY_SCHEMAS_WITH_COLUMNS
    }).single$))?.getCustomEntitySchemas?.items;

    let values = columns.map((c: CustomEntityColumnSchema) => {
      c.entitySchema = c.entitySchema ?? entities.find(e => e.id == c.entitySchemaId);

      return {
        tabLabel: `${c.entitySchema?.displayName ?? c.entitySchema?.identifier} (${c.displayName ?? c.relatedPropertyName ?? c.identifier})`,
        entityName: c.entitySchema?.identifier,
        entitySchema: c.entitySchema,
        silentSearchRules: () => [
          {
            field: c.identifier,
            operator: Operator.equal,
            value: this.model?.id
          }
        ],
        silentDefaultModel: () => ({
          [c.identifier]: this.model?.id
        })
      } as CustomEntityTabInfo;
    });

    this.relatedCustomEntities.set(values);
    return values;
  }
}
